import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { PaperPlaneTilt, X } from "@phosphor-icons/react";

const ChatDrawer = ({ open, onClose }) => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi — I'm Vellum, your firm's AI co-pilot. Ask about an engagement, draft a client note, or describe a mini-app you'd like to build.",
    },
  ]);
  const [input, setInput] = useState("");
  const [convId, setConvId] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const txt = input.trim();
    if (!txt || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: txt }]);
    setSending(true);
    try {
      const r = await api.post("/chat", { message: txt, conversation_id: convId });
      setConvId(r.data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — Vellum is offline. Try again shortly." },
      ]);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-end">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        data-testid="chat-backdrop"
      />
      <div
        className="relative bg-[#F7F6F2] w-full md:w-[420px] md:h-[600px] md:mr-6 border border-[#1A2F24]/20 flex flex-col fade-up"
        data-testid="chat-drawer"
      >
        <div className="flex items-center justify-between p-4 border-b border-[#1A2F24]/12 bg-white">
          <div>
            <p className="label-eyebrow">AI Co-pilot</p>
            <p className="font-serif text-xl text-[#112018] leading-none mt-1">Vellum</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#efece3]"
            data-testid="chat-close"
          >
            <X size={18} />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => (
            <div
              key={i}
              className={m.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"}
              data-testid={`chat-msg-${m.role}`}
            >
              {m.content}
            </div>
          ))}
          {sending && (
            <div className="chat-bubble-ai italic text-[#606C64]">Thinking…</div>
          )}
        </div>

        <div className="p-3 border-t border-[#1A2F24]/12 bg-white flex gap-2">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask Vellum…"
            className="input-clean flex-1"
          />
          <button
            data-testid="chat-send"
            onClick={send}
            disabled={sending}
            className="btn-primary px-4"
          >
            <PaperPlaneTilt size={16} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatDrawer;
